import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { AlertBox, ContentSwitcher, Icon, Spinner } from '@iwoca/orion';
import classname from 'classnames';
import { connect } from 'react-redux';

import { useGetSplitTestQuery } from '@lendingAPI/lendingAPIQueries';
import { getCountryCode } from '@lendingAPI/lendingAPISelector';
import { getAmplitudeDefaultEventProperties } from '@src/tracking/amplitudeEvents';
import { getSplitTestValue } from '@src/utils/selectors';

import styles from './VATStatement.module.css';
import { AppDispatch, RootState } from '../../../reducers';
import {
    ADD_VAT_MISSING_DOCS,
    HANDLE_TRUE_LAYER_LINKED,
    REMOVE_VAT_MISSING_DOCS
} from '../actions';
import { useTrueLayerRedirected } from '../BusinessBankStatement/hooks/useTrueLayerRedirected';
import FileInput from '../FileInput/FileInput';
import UploadedFilesStatus from '../FileUpload/UploadedFilesStatus';
import MissingRequiredDocWarning from '../MissingRequiredDocWarning/MissingRequiredDocWarning';
import { useDocPresentStatus } from '../MissingRequiredDocWarning/MissingRequiredDocWarning.hook';
import { documentTypes } from '../TradingHistory.helpers';
import thstyles from '../TradingHistory.module.css';
import { TGetOffersResponse } from '../types';

const mapStateToProps = (state: RootState) => ({
    missingDocs: state?.tradingHistory?.missing,
    truelayerLoadedOffers: state?.tradingHistory?.truelayerLoadedOffers,
    isPageLoaded: !state.tradingHistory.loading,
    isBusinessDetailsReductionSplitTest:
        getSplitTestValue(state, 'business_details_reduction') &&
        getCountryCode(state) === 'uk',
    selectedOffers: state?.tradingHistory?.selectedOffers
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
    handleTrueLayerLinked: () =>
        dispatch({
            type: HANDLE_TRUE_LAYER_LINKED
        }),
    addMissingDocVAT: () =>
        dispatch({
            type: ADD_VAT_MISSING_DOCS
        }),
    removeMissingDocVAT: () =>
        dispatch({
            type: REMOVE_VAT_MISSING_DOCS
        })
});

const VATStatement = ({
    quantifier,
    isOptional,
    missingDocs,
    truelayerLoadedOffers,
    isPageLoaded,
    isBusinessDetailsReductionSplitTest,
    selectedOffers,
    handleTrueLayerLinked,
    addMissingDocVAT,
    removeMissingDocVAT
}: {
    quantifier: number;
    isOptional: boolean;
    selectedOffers: TGetOffersResponse['data']['offers'][number];
} & Omit<ReturnType<typeof mapStateToProps>, 'selectedOffers'> &
    ReturnType<typeof mapDispatchToProps>) => {
    const { branch } = useGetSplitTestQuery('remove_vat_requirement_test');
    const isVATDocPresent = useDocPresentStatus(missingDocs, 'vat_return');
    const [loadingVAT, setLoadingVAT] = useState(false);
    const [vatStatus, setVatStatus] = useState('yes');
    const offersAutoEligible = selectedOffers?.is_auto_eligible;

    const isBusinessDetailsReduction =
        isBusinessDetailsReductionSplitTest &&
        branch === 'exclude' &&
        !offersAutoEligible;

    const { alertStatus: trueLayerAlertStatus } = useTrueLayerRedirected();

    useEffect(() => {
        if (!isBusinessDetailsReduction) return;

        if (truelayerLoadedOffers) return setLoadingVAT(false);

        if (trueLayerAlertStatus && isPageLoaded) {
            handleTrueLayerLinked();

            return setLoadingVAT(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trueLayerAlertStatus, isPageLoaded, truelayerLoadedOffers]);

    const quarter = quantifier / 3;
    let vatTitle;
    if (quarter > 1) {
        vatTitle = (
            <h3 className={styles.vatTitle}>
                Business VAT returns for the last {quarter} quarters
            </h3>
        );
    } else {
        vatTitle = (
            <h3 className={styles.vatTitle}>
                Business VAT returns for the last quarter
            </h3>
        );
    }

    if (loadingVAT && isBusinessDetailsReduction) {
        return (
            <div className={styles.vatLoaderContainer}>
                <Spinner />
                <p>Updating your requirements</p>
            </div>
        );
    }

    return (
        <>
            {isBusinessDetailsReduction && isOptional !== false && (
                <div
                    className={classname(
                        styles.VATAccount,
                        styles.VATAccountSwitch
                    )}
                    data-testid="vat-statement-question">
                    <div className={thstyles.headerSection}>
                        <h3 className={styles.vatTitle}>
                            Is your business VAT registered?
                        </h3>
                    </div>
                    <div
                        className={classname(
                            styles.descriptionContainer,
                            styles.vatDescriptionContainer
                        )}>
                        <ContentSwitcher
                            value={vatStatus}
                            wrapperClassName={styles.vatSwitchContainer}
                            className={styles.vatSwitch}>
                            <ContentSwitcher.Button
                                id="yes"
                                setSelected={() => {
                                    tracking.ampli.toggle({
                                        ...getAmplitudeDefaultEventProperties(),
                                        optionLabel: 'yes'
                                    });

                                    setVatStatus('yes');

                                    return addMissingDocVAT();
                                }}
                                className={classname(
                                    styles.vatSwitchButton,
                                    vatStatus === 'yes' && styles.boldWeight
                                )}>
                                Yes
                            </ContentSwitcher.Button>
                            <ContentSwitcher.Button
                                id="no"
                                setSelected={() => {
                                    tracking.ampli.toggle({
                                        ...getAmplitudeDefaultEventProperties(),
                                        optionLabel: 'no'
                                    });

                                    setVatStatus('no');

                                    return removeMissingDocVAT();
                                }}
                                className={classname(
                                    styles.vatSwitchButton,
                                    vatStatus === 'no' && styles.boldWeight
                                )}>
                                No
                            </ContentSwitcher.Button>
                        </ContentSwitcher>
                        <div
                            className={classname(styles.alertAnimation, {
                                [styles.infoAnimateIn]: vatStatus === 'no'
                            })}>
                            <AlertBox
                                data-testid="vat-warning"
                                variant="connect"
                                className={classname(styles.infoAlert)}>
                                <Icon
                                    width={25}
                                    height={20}
                                    icon="infoCircleOutline"
                                />
                                <p>
                                    <strong>Important - </strong> selecting the
                                    wrong option will delay your application.
                                </p>
                            </AlertBox>
                        </div>
                    </div>
                </div>
            )}
            <div
                className={classname(styles.VATAccount, {
                    [styles.hideVAT]:
                        isBusinessDetailsReduction && vatStatus === 'no',
                    [styles.showVAT]:
                        isBusinessDetailsReduction && vatStatus === 'yes'
                })}
                data-testid="vat-statement">
                <div className={thstyles.headerSection}>
                    {vatTitle}
                    {isVATDocPresent && vatStatus === 'yes' && (
                        <MissingRequiredDocWarning dataTestId="vat-warning-required" />
                    )}
                </div>
                <div className={styles.VATAccountInfoSection}>
                    <div className={styles.descriptionContainer}>
                        <ul>
                            <li>
                                We accept PDF statements downloaded from either{' '}
                                <a
                                    href="https://www.tax.service.gov.uk/gg/sign-in?continue=/business-account/vat"
                                    target="_blank"
                                    rel="noreferrer">
                                    the HMRC portal
                                </a>{' '}
                                or from your accounting software
                            </li>
                            <li>
                                Need help? Check out our VAT return guide{' '}
                                <a
                                    href="https://static.iwoca.com/pdf/vat_return_instructions.pdf"
                                    target="_blank"
                                    rel="noreferrer">
                                    here
                                </a>{' '}
                            </li>
                        </ul>
                    </div>
                    <FileInput
                        documentType={documentTypes.vat_return}
                        buttonText="Upload returns"
                        isOptional={
                            isBusinessDetailsReduction
                                ? false
                                : isOptional
                                ? true
                                : false
                        }
                        disabled={vatStatus === 'no'}
                    />
                </div>
                <UploadedFilesStatus documentType={documentTypes.vat_return} />
            </div>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(VATStatement);
